import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Tag, ArrowRight, Briefcase, Cpu, Palette, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const CATS = [
  { id: "all", label: "All", icon: ShoppingBag },
  { id: "service", label: "Services", icon: Briefcase },
  { id: "physical", label: "Our Secrets", icon: Palette },
];

const fmt = (price, currency) => {
  if (!price || price === 0) return "Price on Request";
  if (currency === "jmd") return `JMD $${price.toLocaleString()}`;
  return `$${price.toFixed(2)}`;
};

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const filtered = filter === "all" ? products : products.filter(p => p.category === filter);

  const buyProduct = async (product) => {
    if (!product.price || product.price === 0) {
      window.open("https://wa.me/18764390976?text=Hi!%20I'm%20interested%20in%20" + encodeURIComponent(product.name), "_blank");
      return;
    }
    try {
      const res = await axios.post(`${API}/products/${product.id}/checkout`, { origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch { toast.error("Checkout failed."); }
  };

  return (
    <div data-testid="shop-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body uppercase tracking-[0.3em] text-gold mb-4">The Collection</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-none mb-4">Services, Products & <span className="italic text-gold">Luxury.</span></motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/40 font-body text-base max-w-xl">From premium event services and cutting-edge tech solutions to exclusive fragrances and bespoke fashion from Our Secrets Branding.</motion.p>
          </motion.div>

          <div className="flex gap-2 mb-10 flex-wrap">
            {CATS.map(c => (
              <button key={c.id} onClick={() => setFilter(c.id)} className={`flex items-center gap-2 px-5 py-2.5 text-xs font-body transition-all ${filter === c.id ? "bg-gold/15 text-gold border border-gold/30" : "border border-white/10 text-white/40 hover:text-white hover:border-white/20"}`} data-testid={`filter-${c.id}`}>
                <c.icon size={14} /> {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-gold/50 font-body animate-pulse">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20"><ShoppingBag size={48} className="text-white/10 mx-auto mb-4" /><p className="text-white/30 font-body text-sm">No products in this category.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p, i) => (
                <motion.div key={p.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="border border-white/5 bg-white/[0.015] overflow-hidden group hover:border-gold/15 transition-all" data-testid={`product-${p.id}`}>
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <div className="absolute top-3 left-3">
                      <span className={`text-[9px] font-body px-2 py-1 ${p.category === "service" ? "bg-gold/20 text-gold" : p.category === "physical" ? "bg-purple-400/20 text-purple-300" : "bg-blue-400/20 text-blue-300"}`}>
                        {p.category === "service" ? (p.tags?.includes("cartblancja") ? "CartBlancJA" : "Aristocrats JA") : "Our Secrets"}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-white font-body text-sm font-medium mb-2">{p.name}</h3>
                    <p className="text-white/35 font-body text-xs leading-relaxed mb-4 line-clamp-2">{p.description}</p>
                    <div className="flex items-center justify-between">
                      <p className={`font-heading text-lg ${p.price ? "text-gold" : "text-white/50 text-sm italic"}`}>{fmt(p.price, p.currency)}</p>
                      <button onClick={() => buyProduct(p)} className={`flex items-center gap-1.5 text-xs font-body px-4 py-2 transition-all ${p.price ? "bg-gold/15 text-gold hover:bg-gold/25" : "bg-white/5 text-white/50 hover:text-gold hover:bg-gold/10"}`} data-testid={`buy-${p.id}`}>
                        {p.price ? <><Tag size={12} /> Purchase</> : <><MessageCircle size={12} /> Inquire</>}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
